/**
 * Consent-aware GA4 handoff for the Store A -> Store B checkout bridge.
 * Captures only the identifiers needed to connect the server-side purchase to
 * the browser session; payment and customer data never enter this payload.
 */
(function () {
  'use strict';

  var measurementId = String(window.STORE_GA4_MEASUREMENT_ID || '').trim().toUpperCase();
  var GET_TIMEOUT_MS = 250;
  var EVENT_TIMEOUT_MS = 800;
  var privacyApiPromise;

  function getPrivacyApi() {
    var shopify = window.Shopify;
    var existing = shopify && shopify.customerPrivacy;
    if (existing && typeof existing.analyticsProcessingAllowed === 'function') {
      return Promise.resolve(existing);
    }
    if (!shopify || typeof shopify.loadFeatures !== 'function') return Promise.resolve(null);
    if (privacyApiPromise) return privacyApiPromise;
    privacyApiPromise = new Promise(function (resolve) {
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
          var privacy = !error && shopify.customerPrivacy;
          resolve(privacy && typeof privacy.analyticsProcessingAllowed === 'function' ? privacy : null);
        });
      } catch (error) {
        resolve(null);
      }
    });
    return privacyApiPromise;
  }

  function getPermissions() {
    return getPrivacyApi().then(function (privacy) {
      if (!privacy) return null;
      try {
        if (privacy.analyticsProcessingAllowed() !== true) return null;
        var marketing = typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed() === true;
        var saleOfData = typeof privacy.saleOfDataAllowed === 'function' && privacy.saleOfDataAllowed() === true;
        return {
          analytics_storage: 'GRANTED',
          ad_storage: marketing ? 'GRANTED' : 'DENIED',
          ad_user_data: marketing && saleOfData ? 'GRANTED' : 'DENIED',
          ad_personalization: marketing && saleOfData ? 'GRANTED' : 'DENIED',
        };
      } catch (error) {
        return null;
      }
    });
  }

  function readCookie(name) {
    var prefix = name + '=';
    var values = String(document.cookie || '').split(';');
    for (var i = 0; i < values.length; i += 1) {
      var value = values[i].trim();
      if (value.indexOf(prefix) === 0) {
        try {
          return decodeURIComponent(value.slice(prefix.length));
        } catch (error) {
          return value.slice(prefix.length);
        }
      }
    }
    return '';
  }

  function parseClientId(cookieValue) {
    var match = String(cookieValue || '').match(/^(?:GA\d+\.\d+\.)?(\d{1,20}\.\d{1,20})$/);
    return match ? match[1] : '';
  }

  function parseSessionCookie(cookieValue) {
    var value = String(cookieValue || '');
    var modern = value.match(/(?:^|\.)s(\d{1,20})\$o(\d{1,10})(?:\$|$)/);
    if (modern) {
      return { session_id: modern[1], session_number: parseInt(modern[2], 10) || 1 };
    }
    var legacy = value.match(/^GS\d+\.\d+\.(\d{1,20})\.(\d{1,10})(?:\.|$)/);
    if (legacy) {
      return { session_id: legacy[1], session_number: parseInt(legacy[2], 10) || 1 };
    }
    return {};
  }

  function sessionCookieName() {
    if (!/^G-[A-Z0-9]+$/.test(measurementId)) return '';
    return '_ga_' + measurementId.slice(2);
  }

  function getGtagValue(field) {
    return new Promise(function (resolve) {
      if (!measurementId || typeof window.gtag !== 'function') {
        resolve('');
        return;
      }
      var settled = false;
      var timer = window.setTimeout(function () {
        if (!settled) {
          settled = true;
          resolve('');
        }
      }, GET_TIMEOUT_MS);
      try {
        window.gtag('get', measurementId, field, function (value) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve(value == null ? '' : String(value));
        });
      } catch (error) {
        window.clearTimeout(timer);
        settled = true;
        resolve('');
      }
    });
  }

  function getContext() {
    return getPermissions().then(function (permissions) {
      if (!permissions) return null;
      var clientId = parseClientId(readCookie('_ga'));
      var session = parseSessionCookie(readCookie(sessionCookieName()));
      var clientPromise = clientId ? Promise.resolve(clientId) : getGtagValue('client_id');
      var sessionPromise = session.session_id ? Promise.resolve(session.session_id) : getGtagValue('session_id');

      return Promise.all([clientPromise, sessionPromise]).then(function (values) {
        var normalizedClientId = parseClientId(values[0]);
        var normalizedSessionId = /^\d{1,20}$/.test(values[1]) ? values[1] : '';
        if (!normalizedClientId) return null;
        var context = {
          client_id: normalizedClientId,
          consent: permissions,
        };
        if (normalizedSessionId) context.session_id = normalizedSessionId;
        if (session.session_number) context.session_number = session.session_number;
        return context;
      });
    });
  }

  function enrichPayload(payload) {
    if (!payload || typeof payload !== 'object') return Promise.resolve(payload);
    return getContext().then(function (context) {
      if (context) payload.analytics = context;
      return payload;
    });
  }

  function ecommerceItems(payload) {
    return (payload.items || []).map(function (item) {
      var quantity = Math.max(1, parseInt(item.quantity, 10) || 1);
      var cents = Number(item.price);
      if (!isFinite(cents)) cents = 0;
      var analyticsItem = {
        item_id: String(item.variant_id || item.id || ''),
        item_name: String(item.title || 'Item'),
        price: Math.round(cents) / 100,
        quantity: quantity,
      };
      if (item.sku) analyticsItem.item_variant = String(item.sku);
      return analyticsItem;
    });
  }

  function trackBeginCheckout(payload) {
    return getPermissions().then(function (permissions) {
      if (!permissions || typeof window.gtag !== 'function') return false;
      var items = ecommerceItems(payload || {});
      if (!items.length) return false;
      var currency = String(
        (payload && payload.presentment_currency) ||
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        'GBP'
      ).toUpperCase();
      var value = items.reduce(function (total, item) {
        return total + (Number(item.price) * Number(item.quantity));
      }, 0);

      return new Promise(function (resolve) {
        var settled = false;
        function finish() {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve(true);
        }
        var timer = window.setTimeout(finish, EVENT_TIMEOUT_MS + 50);
        try {
          window.gtag('event', 'begin_checkout', {
            currency: currency,
            value: Math.round(value * 100) / 100,
            items: items,
            event_callback: finish,
            event_timeout: EVENT_TIMEOUT_MS,
          });
        } catch (error) {
          finish();
        }
      });
    });
  }

  window.StoreCheckoutAnalytics = {
    getContext: getContext,
    enrichPayload: enrichPayload,
    trackBeginCheckout: trackBeginCheckout,
  };
})();
